/**
 * Feature: PLANES DE LECTURA — lógica PURA (sin UI, sin DB).
 *
 * Un plan es una plantilla ESTÁTICA (ver `data/plan-templates.ts`): una
 * secuencia de días, cada uno con 1..n capítulos. El único estado persistido es
 * el PROGRESO del usuario (`bible_reading_progress`: una fila por día
 * completado, soft-delete con `deleted_at`). Estas funciones derivan la vista
 * (días hechos, siguiente día, porcentaje) de esas filas. Puras y
 * deterministas: mismos datos → mismo resultado.
 */

/** Un capítulo concreto a leer (coordenada canónica libro/capítulo). */
export interface PlanReading {
  book: number;
  chapter: number;
}

/** Un día del plan: uno o más capítulos contiguos. */
export interface PlanDay {
  readings: PlanReading[];
}

/**
 * Fila mínima de progreso (proyección de `bible_reading_progress`). `day` es el
 * índice 1-based dentro del plan; filas soft-borradas NO deben llegar aquí (el
 * caller filtra `deleted_at IS NULL`).
 */
export interface PlanProgressRow {
  plan_id: string | null;
  day: number | null;
}

/** Vista derivada del avance de un plan. */
export interface PlanProgressView {
  /** Días completados (dedupe multi-dispositivo), acotados a los días válidos del plan. */
  completedDays: ReadonlySet<number>;
  completedCount: number;
  totalDays: number;
  /** 0..100, redondeado. */
  percent: number;
  /** Primer día NO completado (1-based); `null` si el plan está terminado. */
  nextDay: number | null;
  isComplete: boolean;
  /** El plan tiene al menos un día completado. */
  isStarted: boolean;
}

/**
 * Días completados de UN plan a partir de las filas de progreso: filtra por
 * `plan_id`, descarta días fuera de rango (plantillas pueden evolucionar) y
 * dedupe (dos dispositivos pueden haber marcado el mismo día offline).
 */
export function completedDaysOf(
  rows: readonly PlanProgressRow[],
  planId: string,
  totalDays: number,
): Set<number> {
  const days = new Set<number>();
  for (const row of rows) {
    if (row.plan_id !== planId || row.day === null) {
      continue;
    }
    if (Number.isInteger(row.day) && row.day >= 1 && row.day <= totalDays) {
      days.add(row.day);
    }
  }
  return days;
}

const PERCENT_MAX = 100;

/** Deriva la vista de avance de un plan (puro). */
export function computePlanProgress(
  rows: readonly PlanProgressRow[],
  planId: string,
  totalDays: number,
): PlanProgressView {
  const completedDays = completedDaysOf(rows, planId, totalDays);
  const completedCount = completedDays.size;
  let nextDay: number | null = null;
  for (let day = 1; day <= totalDays; day += 1) {
    if (!completedDays.has(day)) {
      nextDay = day;
      break;
    }
  }
  const isComplete = totalDays > 0 && completedCount >= totalDays;
  return {
    completedDays,
    completedCount,
    totalDays,
    percent: totalDays === 0 ? 0 : Math.round((completedCount / totalDays) * PERCENT_MAX),
    nextDay,
    isComplete,
    isStarted: completedCount > 0,
  };
}

/**
 * Reparte los capítulos (en orden canónico) en `dayCount` días CONTIGUOS de
 * tamaño equilibrado (difieren a lo sumo en 1, los días más cargados primero).
 * Es el generador de las plantillas secuenciales.
 */
export function splitIntoDays(readings: readonly PlanReading[], dayCount: number): PlanDay[] {
  if (dayCount <= 0) {
    return [];
  }
  const days: PlanDay[] = [];
  const base = Math.floor(readings.length / dayCount);
  const remainder = readings.length % dayCount;
  let cursor = 0;
  for (let i = 0; i < dayCount; i += 1) {
    const size = base + (i < remainder ? 1 : 0);
    days.push({ readings: readings.slice(cursor, cursor + size) });
    cursor += size;
  }
  return days;
}
